<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateFinancePaymentOverdueForm from './RealEstateFinancePaymentOverdueForm.vue';
import { isFinanceBoletoOverdue } from '../composables/useRealEstateFinancePayments';
import { displayReferenceDate } from './postSaleDate';

const props = defineProps({
  loading: { type: Boolean, default: false },
  responsibility: { type: Object, default: null },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const paidOn = ref('');
const interest = ref('');
const penalty = ref('');
const paymentTiming = ref('');

const overdue = computed(() => {
  return isFinanceBoletoOverdue(props.responsibility?.data_validade);
});
const requiresLateDetails = computed(
  () => overdue.value && paymentTiming.value === '1'
);
const valid = computed(
  () =>
    !overdue.value ||
    paymentTiming.value === '2' ||
    (requiresLateDetails.value &&
      paidOn.value.length === 10 &&
      interest.value &&
      penalty.value)
);

const parseAmount = value => {
  const source = String(value || '').replace(/[^\d,.-]/g, '');
  const normalized = source.includes(',')
    ? source.replace(/\./g, '').replace(',', '.')
    : source;
  return Number(normalized) || 0;
};
const finalPaidAmount = computed(() => {
  const amount =
    parseAmount(props.responsibility?.valor_pagamento?.amount) +
    parseAmount(interest.value) +
    parseAmount(penalty.value);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
});

watch(
  () => props.show,
  show => {
    if (!show) return;
    paidOn.value = displayReferenceDate(new Date().toISOString().slice(0, 10));
    interest.value = '';
    penalty.value = '';
    paymentTiming.value = '';
  },
  { immediate: true }
);

const confirm = () => {
  if (!valid.value) return;
  const payload = { confirmed: true };
  if (!overdue.value) {
    emit('confirm', payload);
    return;
  }

  payload.motivo = paymentTiming.value;
  if (requiresLateDetails.value) {
    payload.valor_pago = props.responsibility?.valor_pagamento?.amount;
    payload.valor_juros = interest.value;
    payload.valor_multa = penalty.value;
    payload.data_pagamento = paidOn.value;
    payload.valor_pago_atrasado = finalPaidAmount.value;
  }
  emit('confirm', payload);
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="
      overdue
        ? t('REAL_ESTATE_FINANCE.PAYMENT.LATE_TITLE')
        : t('REAL_ESTATE_FINANCE.PAYMENT.TITLE')
    "
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <p class="text-sm text-lais-text-muted">
        {{ responsibility?.usuario_nome }}
        {{ t('REAL_ESTATE_FINANCE.DETAIL.SEPARATOR') }}
        {{ responsibility?.valor_pagamento?.amount }}
      </p>

      <RealEstateFinancePaymentOverdueForm
        v-if="overdue"
        :responsibility="responsibility"
        :requires-late-details="requiresLateDetails"
        :payment-timing="paymentTiming"
        :interest="interest"
        :penalty="penalty"
        :paid-on="paidOn"
        :final-paid-amount="finalPaidAmount"
        @update-payment-timing="paymentTiming = $event"
        @update-interest="interest = $event"
        @update-penalty="penalty = $event"
        @update-paid-on="paidOn = $event"
      />
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('REAL_ESTATE_FINANCE.ACTIONS.CLOSE') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          :disabled="!valid"
          :is-loading="loading"
          @click="confirm"
        >
          {{
            overdue
              ? t('REAL_ESTATE_FINANCE.PAYMENT.FEEDBACK')
              : t('REAL_ESTATE_FINANCE.PAYMENT.CONFIRM')
          }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
